import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Download, FileText, CheckCircle, Mail } from 'lucide-react';

// Gated brochure download: captures the visitor's email (stored as a
// NewsletterSubscriber lead tagged source='corporate_brochure' for sales
// follow-up) before triggering the pre-uploaded PDF download.
export default function BrochureDownloadModal({ open, onClose, pdfUrl }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setEmail('');
    setName('');
    setCompany('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await base44.entities.NewsletterSubscriber.create({
        email: email.trim().toLowerCase(),
        source: 'corporate_brochure',
      });
    } catch (err) {
      // Duplicate email is fine — they may already be subscribed.
      // Proceed to deliver the brochure regardless.
    }
    setLoading(false);
    setSuccess(true);
  };

  const handleDownload = () => {
    window.location.href = '/functions/downloadCorporateBrochure';
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.25 }}
            className="relative w-full max-w-md bg-[#0d0d0f] border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 text-white/40 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                {!success ? (
                  <motion.div
                    key="capture"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-primary/10 border border-primary/20 mb-5">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-primary text-[10px] font-bold tracking-[0.3em] mb-2">CORPORATE BROCHURE</p>
                    <h3 className="text-white font-black text-xl uppercase leading-tight mb-2">Download Our Pricing Guide</h3>
                    <p className="text-white/40 text-xs leading-relaxed mb-6">
                      Enter your details and we'll send you straight to our corporate pricing tiers and package options. We'll also keep you in the loop with future B2B offers.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1.5">NAME</label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          className="w-full bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1.5">COMPANY</label>
                        <input
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          placeholder="Company name"
                          className="w-full bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-white/30 text-[10px] font-bold tracking-widest mb-1.5">EMAIL *</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full bg-[#111115] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none transition-colors"
                          autoFocus
                        />
                      </div>

                      {error && <p className="text-red-400 text-xs font-medium">{error}</p>}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest py-3.5 transition-colors disabled:opacity-50"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        GET THE BROCHURE
                      </button>
                    </form>

                    <p className="text-white/20 text-[10px] text-center mt-4 flex items-center justify-center gap-1.5">
                      <Mail className="w-3 h-3" />
                      We'll only use your email to share corporate packages and offers.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-center py-4"
                  >
                    <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                    <h3 className="text-white font-black text-xl uppercase mb-2">You're All Set</h3>
                    <p className="text-white/50 text-sm mb-6 leading-relaxed">
                      {pdfUrl
                        ? "Your corporate brochure is ready to download. We've also sent a copy to your inbox."
                        : "Thanks for your interest! Our team will email your corporate brochure shortly."}
                    </p>
                    {pdfUrl && (
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors mb-4"
                      >
                        <Download className="w-4 h-4" /> DOWNLOAD PDF
                      </button>
                    )}
                    <div>
                      <button
                        onClick={handleClose}
                        className="text-white/40 hover:text-white text-xs font-bold tracking-widest transition-colors"
                      >
                        CLOSE
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}